import { NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useMe, useRole } from "@/api/hooks";
import { useAuth } from "@/context/AuthContext";
import { NAV, DEFAULT_ROUTE } from "@/config/nav";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet, SheetContent, SheetDescription, SheetTitle,
} from "@/components/ui/sheet";
import { Ship, ChevronsUpDown, Check, LogOut, Eye, Search } from "lucide-react";
import { CommandCenter } from "@/components/layout/CommandCenter";

const ROLE_ORDER = ["sales", "operations", "finance", "management", "analytics"];
const SHORT = { sales: "Sales", operations: "Operations", finance: "Finance", management: "Management", analytics: "Analytics", admin: "Admin" };
const FULLNAME = { sales: "Sales & Customer Support", operations: "Operations", finance: "Finance", management: "Management", analytics: "Analytics & Performance" };

export function Sidebar({ mobileOpen = false, onMobileOpenChange = () => {} }) {
  const navigate = useNavigate();
  const role = useRole();
  const { data: me } = useMe();
  const { logout } = useAuth();
  const [commandOpen, setCommandOpen] = useState(false);
  const items = NAV[role] || [];
  const user = me?.user;
  const roles = me?.roles || [];
  const isAdmin = me?.isAdmin;

  const renderContent = (closeNavigation, isMobile = false) => (
    <>
      <div className={`flex items-center gap-2.5 border-b border-white/10 px-4 py-4 ${isMobile ? "pr-12" : ""}`}>
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-blue-600"><Ship className="h-4 w-4 text-white" strokeWidth={2} /></div>
        <div className="leading-tight"><div className="font-display text-[15px] font-bold tracking-tight">JVK FreightOps</div><div className="mt-0.5 text-[10px] uppercase tracking-wider text-slate-400">Operating system</div></div>
      </div>

      <div className="px-3 pt-3">
        <div className="mb-1.5 px-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{isAdmin ? "Workspace · viewing as" : "Workspace"}</div>
        {isAdmin ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid={isMobile ? "mobile-role-switcher" : "role-switcher"} className="flex w-full items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-2.5 py-2 transition-colors hover:bg-slate-700">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-indigo-500/20 text-xs font-bold text-indigo-300">{SHORT[role]?.[0] || "W"}</span>
                <span className="flex-1 truncate text-left text-sm font-semibold">{SHORT[role] || "Workspace"}</span>
                <ChevronsUpDown className="h-3.5 w-3.5 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              {ROLE_ORDER.map((nextRole) => {
                const roleRecord = roles.find((entry) => entry.id === nextRole);
                return (
                  <DropdownMenuItem key={nextRole} data-testid={`${isMobile ? "mobile-" : ""}role-option-${nextRole}`} onClick={() => { closeNavigation(); navigate(DEFAULT_ROUTE[nextRole]); }} className="cursor-pointer">
                    <div className="flex-1"><div className="text-sm font-medium">{SHORT[nextRole]}</div><div className="text-[11px] text-slate-400">{roleRecord ? roleRecord.name : FULLNAME[nextRole]}</div></div>
                    {nextRole === role && <Check className="h-4 w-4 text-indigo-600" />}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <div className="flex w-full items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-2.5 py-2">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-indigo-500/20 text-xs font-bold text-indigo-300">{SHORT[role]?.[0] || "W"}</span>
            <span className="flex-1 truncate text-left text-sm font-semibold">{SHORT[role] || "Workspace"}</span>
          </div>
        )}
        {isAdmin && <div className="mt-1.5 flex items-center gap-1 px-1 text-[10px] text-violet-300"><Eye className="h-3 w-3" />Admin preview — actions logged as you</div>}
      </div>

      <div className="px-3 pt-2">
        <button onClick={() => { closeNavigation(); setCommandOpen(true); }} data-testid={isMobile ? "mobile-search-btn" : "global-search-btn"}
          className="flex h-9 w-full items-center gap-2 rounded-lg border border-slate-700 bg-slate-800/70 px-2.5 text-sm text-slate-300 transition-colors hover:bg-slate-800 hover:text-white">
          <Search className="h-4 w-4" /><span className="flex-1 text-left">Search workspace</span>
          <kbd className="rounded border border-slate-600 px-1.5 py-0.5 text-[10px] text-slate-400">Ctrl K</kbd>
        </button>
      </div>

      <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-4" aria-label="Workspace navigation">
        {items.map((item) => (
          <NavLink key={item.to} to={item.to} onClick={closeNavigation} data-testid={`${isMobile ? "mobile-" : ""}nav-${item.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
            className={({ isActive }) => `flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium transition-colors ${isActive ? "bg-blue-600 text-white shadow-sm" : "text-slate-300 hover:bg-white/10 hover:text-white"}`}>
            <item.icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />{item.label}
          </NavLink>
        ))}
      </nav>

      <div className="flex items-center gap-2.5 border-t border-slate-800 px-3 py-3">
        {user?.avatar ? <img src={user.avatar} alt={user.name} className="h-8 w-8 rounded-full object-cover" /> : user ? <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-700 text-xs font-semibold text-slate-100" aria-hidden="true">{(user.name || "U").slice(0, 1).toUpperCase()}</div> : null}
        <div className="min-w-0 flex-1 leading-tight"><div className="truncate text-sm font-medium">{user?.name || "User"}</div><div className="truncate text-[11px] text-slate-400">{user?.title}</div></div>
        <button onClick={() => { closeNavigation(); logout(); }} data-testid={isMobile ? "mobile-logout-btn" : "logout-btn"} title="Sign out" aria-label="Sign out" className="rounded p-1 text-slate-400 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"><LogOut className="h-4 w-4" /></button>
      </div>
    </>
  );

  return (
    <>
      <aside className="hidden w-64 shrink-0 flex-col border-r border-[#1b3558] bg-[#10233f] text-slate-100 lg:sticky lg:top-0 lg:flex lg:h-screen">
        {renderContent(() => {})}
      </aside>
      <Sheet open={mobileOpen} onOpenChange={onMobileOpenChange}>
        <SheetContent
          id="mobile-workspace-navigation"
          side="left"
          className="w-[min(20rem,88vw)] max-w-none overflow-hidden border-[#1b3558] bg-[#10233f] p-0 text-slate-100 [&>button]:right-3 [&>button]:top-4 [&>button]:z-10 [&>button]:text-slate-300 [&>button]:ring-offset-[#10233f] [&>button]:hover:text-white lg:hidden"
        >
          <SheetTitle className="sr-only">Workspace navigation</SheetTitle>
          <SheetDescription className="sr-only">Navigate between pages, search records, or sign out.</SheetDescription>
          <div className="flex h-full min-h-0 flex-col">
            {renderContent(() => onMobileOpenChange(false), true)}
          </div>
        </SheetContent>
      </Sheet>
      <CommandCenter open={commandOpen} onOpenChange={setCommandOpen} />
    </>
  );
}
